<template>
  <!-- Intercom logic container - hidden anchor -->
  <div style="display: none;" aria-hidden="true"/>
</template>

<script setup lang="ts">
import { watch, onMounted, onUnmounted } from 'vue'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { hasConsent, onConsentChange } from '@/shared/consent'

const profileStore = useProfileStore()

/**
 * Intercom Widget Integration for GonkaGame
 * 
 * Initializes Intercom chat support widget with user context.
 * Widget is positioned outside the main app (via z-index layering)
 * and includes a "Техподдержка" label.
 */

const INTERCOM_APP_ID = 'hbzm0yre'

declare global {
  interface Window {
    Intercom: any
    intercomSettings: Record<string, any>
    GetAccessCallback?: () => void
  }
}

function getFingerprint(): string | null {
  return window.sessionStorage.getItem('_fp')
}

function initIntercomSettings() {
  const profile = profileStore.profile
  if (!profile) return

  // Set up GetAccessCallback (for potential store redirect)
  window.GetAccessCallback = () => {
    // Can be customized: window.location.href = '/app/store/67'
  }

  // Configure Intercom with user data
  window.intercomSettings = {
    app_id: INTERCOM_APP_ID,
    user_id: String(profile.id),
    // Identity verification hash (generated server-side)
    user_hash: profile.intercom_user_hash,
    name: profileStore.fullName,
    first_name: profile.first_name,
    email: profile.email,
    created_at: profile.regtime ? Math.floor(new Date(profile.regtime).getTime() / 1000) : undefined,
    Status: profile.access_title,
    domain: window.location.host,
    // ip is typically set server-side
    fpid: getFingerprint()
  }
}

function loadIntercomScript(): void {
  const w = window as any
  const ic = w.Intercom

  if (typeof ic === 'function') {
    ic('reattach_activator')
    ic('update', w.intercomSettings)
  } else {
    const d = document
    const i = function (...args: any[]) {
      (i as any).c(args)
    } as any
    i.q = [] as any[]
    i.c = function (args: any) {
      i.q.push(args)
    }
    w.Intercom = i

    const l = function () {
      const s = d.createElement('script')
      s.type = 'text/javascript'
      s.async = true
      s.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`
      const x = d.getElementsByTagName('script')[0]
      x.parentNode?.insertBefore(s, x)
    }

    if (document.readyState === 'complete') {
      l()
    } else {
      window.addEventListener('load', l, false)
    }
  }
}

function addSupportLabel(): void {
  const checkAndAddLabel = () => {
    const launcher = document.querySelector('.intercom-lightweight-app-launcher-icon')
    if (!launcher) {
      setTimeout(checkAndAddLabel, 300)
      return
    }

    const existingLabel = document.querySelector('.intercom-text')
    if (!existingLabel) {
      const label = document.createElement('div')
      label.className = 'intercom-text'
      label.textContent = 'Техподдержка'
      launcher.appendChild(label)
    }
  }

  checkAndAddLabel()
}

function initIntercom(): void {
  initIntercomSettings()
  loadIntercomScript()
  
  // Add custom label after a short delay to ensure widget is loaded
  setTimeout(addSupportLabel, 1000)
}

function shutdownIntercom(): void {
  if (window.Intercom) {
    window.Intercom('shutdown')
  }
}

/**
 * Чат поддержки — необязательная категория «Поддержка и диагностика»
 * (`/law/services`): виджет получает имя, адрес почты и отпечаток браузера,
 * поэтому до согласия его скрипт не грузится вовсе. Условий два — профиль
 * загружен И согласие дано; какое из них выполнится позже, неважно.
 */
function initIntercomWhenAllowed(): void {
  if (!hasConsent('support')) return
  if (!profileStore.profile) return
  initIntercom()
}

watch(() => profileStore.profile, initIntercomWhenAllowed, { immediate: true })

const stopConsentWatch = onConsentChange((state) => {
  if (state.support) {
    initIntercomWhenAllowed()
    return
  }
  // Согласие отозвали — гасим виджет в этой же сессии, не дожидаясь перезагрузки.
  shutdownIntercom()
})

onMounted(() => {
  // Inject styles for widget positioning and label
  injectStyles()
})

onUnmounted(() => {
  stopConsentWatch()
  shutdownIntercom()
})

function injectStyles(): void {
  const styleId = 'intercom-custom-styles'
  if (document.getElementById(styleId)) return

  const style = document.createElement('style')
  style.id = styleId
  style.textContent = `
    .intercom-lightweight-app-launcher {
      right: 120px !important;
    }
    
    .act-welcome .intercom-lightweight-app-launcher {
      right: auto !important;
      left: 20px !important;
    }
    
    .intercom-text {
      position: absolute;
      bottom: -24px;
      left: 50%;
      transform: translateX(-50%);
      font-size: var(--p-text-11);
      font-weight: var(--ds-weight-medium);
      color: var(--hm-text-secondary);
      white-space: nowrap;
      pointer-events: none;
    }
  `
  document.head.appendChild(style)
}
</script>
